/**
 * Nova server functions -- thin wrappers around server-side logic.
 *
 * Aliases: nova functions, nova api, nova server functions
 */

import { createServerFn } from "@tanstack/react-start";

import { requireNovaCoachAuth } from "~/lib/coach-middleware";
import type { CoachAccountCoach } from "~/lib/server/coach-account";
// Type-only: erased at build, so no server module reaches the client bundle.
import type { GateConversation } from "~/lib/server/nova/assets/gate";
import type { JsonValue } from "~/lib/shared/json";
import { updateDeliverableContentSchema } from "~/lib/shared/nova/deliverable-edit-config";
import type { PlanStepChip } from "~/lib/shared/nova/plan-steps";
import {
  adaptDeliverableSchema,
  applyDeliverableSchema,
  continueNovaConversationSchema,
  createNovaConversationSchema,
  getNovaConversationsSchema,
  getNovaMessagesSchema,
  loadProfileValuesSchema,
  renameNovaConversationSchema,
  updateNovaQueueItemStatusSchema,
} from "~/lib/shared/nova/schemas";
import { novaWorkspaceChangesInputSchema } from "~/lib/shared/nova/workspace-changes";

export const createNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(createNovaConversationSchema)
  .handler(async ({ data, context }) => {
    // Per-agent gate: walled agents require access; free agents (Diagnose)
    // always pass. Covers the `?start=` deep-link, which routes here.
    const { isFreeNovaAgent } = await import("~/lib/shared/nova/agents");
    if (!isFreeNovaAgent(data.agentType)) {
      const { getNovaAccess, NovaAccessDeniedError } =
        await import("~/lib/server/nova/access");
      if (!(await getNovaAccess(context.coach)).allowed) {
        throw new NovaAccessDeniedError();
      }
    }
    // The catalog rule: which ROOM the coach's lifecycle opens.
    const { assertNovaAccess } = await import("~/lib/server/nova/assets/gate");
    await assertNovaAccess({
      coach: context.coach,
      thing: { kind: "conversation", agentType: data.agentType },
    });
    const { startConversation } =
      await import("~/lib/server/nova/conversations");
    return startConversation({
      coachId: context.coach.id,
      agentType: data.agentType,
      title: data.title,
    });
  });

export const continueNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(continueNovaConversationSchema)
  .handler(async ({ data, context }) => {
    const { getConversation, continueConversation } =
      await import("~/lib/server/nova/conversations");
    // Gate the SOURCE before anything: the continuation carries its
    // `extractedData` forward into the returned row, and `summarizeContinuation`
    // spends a model call. Both are the source thread's work.
    const source = await getConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    if (!source) {
      throw new Error("Conversation not found");
    }
    await assertConversationReadable(context.coach, source);

    const conversation = await continueConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    if (!conversation) {
      throw new Error("Conversation not found");
    }
    return conversation;
  });

export const getNovaConversations = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaConversationsSchema)
  .handler(async ({ data, context }) => {
    const { listConversations } =
      await import("~/lib/server/nova/conversations");
    return redactConversationList(
      context.coach,
      await listConversations({
        coachId: context.coach.id,
        agentType: data.agentType,
      }),
    );
  });

/**
 * `listConversations` does a bare `select()`, so a list row carries the same
 * `extractedData` and `metadata` the single-conversation gate protects. Both
 * list server functions go through here, so the list and the single read
 * cannot disagree about what a coach may see.
 *
 */
async function redactConversationList<T extends GateConversation>(
  coach: CoachAccountCoach,
  rows: T[],
): Promise<(T & { locked: boolean })[]> {
  const { redactUnreadableConversations } =
    await import("~/lib/server/nova/assets/gate");
  return redactUnreadableConversations(coach, rows);
}

/**
 * Returns the conversation, or `null` when it doesn't exist / isn't the
 * coach's. A missing conversation (stale link, deleted session) is expected,
 * not a bug — returning null lets the route loader render notFound and keeps
 * the global Sentry function middleware from paging.
 *
 * `activeTurn` is the turn running in the conversation now, from this tab or
 * any other, so a client can hold its own send until it ends.
 */
export const getNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { getConversation } = await import("~/lib/server/nova/conversations");
    const conversation = await getConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    if (!conversation) return null;
    const { findActiveNovaTurnForConversation } =
      await import("~/lib/server/nova/turns");
    const [, activeTurn] = await Promise.all([
      assertConversationReadable(context.coach, conversation),
      findActiveNovaTurnForConversation({
        coachId: context.coach.id,
        conversationId: conversation.id,
      }),
    ]);
    const [{ conversationWorkingAssets }, { conversationCollectionItem }] =
      await Promise.all([
        import("~/lib/server/nova/assets/conversation-signal"),
        import("~/lib/server/nova/assets/collection"),
      ]);
    const workingAssets = conversationWorkingAssets(conversation);
    return {
      ...conversation,
      workingAssets,
      // The post the conversation writes, for the session rail.
      collectionItem: await conversationCollectionItem({
        coachId: context.coach.id,
        conversationId: conversation.id,
        workingAssets,
      }),
      activeTurn,
    };
  });

/**
 * The read gate for one conversation's CONTENT — its messages and its
 * `extractedData`. A churned coach's work is saved, not open, and a pre-trial
 * coach's threads are held the same way. The conversation LIST
 * (`getNovaConversations`) is untouched: names and status stay readable.
 *
 */
async function assertConversationReadable(
  coach: CoachAccountCoach,
  conversation: GateConversation,
): Promise<void> {
  const { assertNovaConversationAccess } =
    await import("~/lib/server/nova/assets/gate");
  await assertNovaConversationAccess({ coach, conversation });
}

/**
 * Resolve the plan-spine router step for a Plan conversation (explicit
 * `plan_router` record when still enterable, else derived coach-scoped from
 * artifacts). Returns null for missing conversations and non-plan agents.
 */
export const getNovaPlanStep = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { getConversation } = await import("~/lib/server/nova/conversations");
    const conversation = await getConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    if (!conversation || conversation.agentType !== "plan") {
      return null;
    }
    // The recap in the router record is work content. Null, not a throw: this
    // fn already answers null for several "nothing to resume" cases and the
    // client degrades to its defaults.
    const { checkNovaConversationAccess } =
      await import("~/lib/server/nova/assets/gate");
    const readable = await checkNovaConversationAccess({
      coach: context.coach,
      conversation,
    });
    if (!readable.allowed) return null;
    const { resolvePlanRouterStep } =
      await import("~/lib/server/nova/agents/plan/router");
    return resolvePlanRouterStep({
      coachId: context.coach.id,
      extractedData: conversation.extractedData ?? null,
    });
  });

/**
 * Combined deliverable read — the coach's latest deliverables AND their Plan
 * progress from ONE deliverables scan (they are two reductions over the same
 * rows; see `getHomeDeliverableStateForCoach`). One server fn so its
 * consumers share a single request and cache entry instead of firing two
 * near-identical full scans.
 *
 * `planProgress` carries the Plan per-step chip states (not
 * started / current / complete / verdict-stale, enabled steps only) plus the
 * central Plan-completion state (`isPlanComplete` — a core_offer exists).
 * Completion derives from durable artifacts; the coach's Plan head is looked
 * up server-side only to align the "current" chip with where a session
 * resumes.
 */
export const getNovaHomeDeliverableState = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { getHomeDeliverableStateForCoach, foundationConfirmationState } =
      await import("~/lib/server/nova/deliverables");
    const { deliverables, derivation } = await getHomeDeliverableStateForCoach(
      context.coach.id,
    );

    const { resolvePlanProgressForCoach } =
      await import("~/lib/server/nova/agents/plan/router");
    const planProgress: { complete: boolean; steps: PlanStepChip[] } =
      await resolvePlanProgressForCoach(context.coach.id, derivation);

    // Rows the coach's lifecycle does not open are dropped rather than
    // emptied: the home shows what she has through `getCoachAssets`, and the
    // content behind a held row never leaves the server.
    const { novaDeliverableReadFilter } =
      await import("~/lib/server/nova/assets/gate");
    const readable = await novaDeliverableReadFilter(context.coach);

    return {
      deliverables: deliverables
        .filter((d) => readable(d.type))
        .map((d) => ({
          id: d.id,
          type: d.type,
          status: d.status,
          version: d.version,
          // Keep-if-cancel: a Business Map that has already been drawn stays
          // whole for the coach it belongs to, whatever her subscription is
          // doing. Drawing a new one is what costs money, and that gate lives
          // on `generateBusinessMapFn`.
          data: d.data as Record<string, JsonValue>,
          lockedAt: d.lockedAt,
          // Coach-facing confirmation state (foundation types only; null else).
          confirmation: foundationConfirmationState(d),
          updatedAt: d.updatedAt,
          createdAt: d.createdAt,
          conversationId: d.conversationId,
        })),
      planProgress,
    };
  });

/**
 * Returns the conversation's UI messages, or an empty array when the
 * conversation doesn't exist / isn't the coach's. A missing conversation has
 * no messages, so an empty array is the correct (and Sentry-quiet) answer.
 */
export const getNovaMessages = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { getConversation } = await import("~/lib/server/nova/conversations");
    const conversation = await getConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    if (!conversation) {
      return [];
    }
    await assertConversationReadable(context.coach, conversation);

    const { loadMessages, toUIMessages } =
      await import("~/lib/server/nova/messages");
    const messages = await loadMessages(data.conversationId);
    return toUIMessages(messages);
  });

export const getNovaDeliverables = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { getConversation } = await import("~/lib/server/nova/conversations");
    const conversation = await getConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    if (!conversation) {
      throw new Error("Conversation not found");
    }

    const { getDeliverables } = await import("~/lib/server/nova/deliverables");
    const deliverables = await getDeliverables({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });

    const { novaDeliverableReadFilter } =
      await import("~/lib/server/nova/assets/gate");
    const readable = await novaDeliverableReadFilter(context.coach);

    return deliverables
      .filter((d) => readable(d.type))
      .map((d) => ({
        id: d.id,
        type: d.type,
        status: d.status,
        version: d.version,
        data: d.data as Record<string, JsonValue>,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      }));
  });

/** Ordered, owner-scoped replay used to converge every NOVA workspace view. */
export const getNovaWorkspaceChanges = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(novaWorkspaceChangesInputSchema)
  .handler(async ({ data, context }) => {
    const { listNovaWorkspaceChanges } =
      await import("~/lib/server/nova/workspace-changes");
    return listNovaWorkspaceChanges({
      coachId: context.coach.id,
      afterSequence: data.afterSequence,
    });
  });

export const updateNovaQueueItemStatus = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(updateNovaQueueItemStatusSchema)
  .handler(async ({ data, context }) => {
    const { updateQueueStatus, resolveQueueItem } =
      await import("~/lib/server/nova/queue");
    const updated = await updateQueueStatus({
      queueItemId: data.queueItemId,
      coachId: context.coach.id,
      status: data.status,
      reviewNotes: data.reviewNotes ?? null,
    });
    if (!updated) {
      throw new Error("Queue item not found");
    }
    const offer =
      updated.kind === "mini_sprint"
        ? (await resolveQueueItem(updated)).offer
        : null;
    return {
      success: true as const,
      status: updated.status,
      metadata: updated.metadata ?? null,
      offer,
    };
  });

export const renameNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(renameNovaConversationSchema)
  .handler(async ({ data, context }) => {
    const { updateConversation } =
      await import("~/lib/server/nova/conversations");
    const updated = await updateConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
      updates: { title: data.title },
    });
    if (!updated) {
      throw new Error("Conversation not found");
    }
    return { id: updated.id, title: updated.title };
  });

export const deleteNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { deleteConversation } =
      await import("~/lib/server/nova/conversations");
    await deleteConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    return { success: true };
  });

export const archiveNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { archiveConversation } =
      await import("~/lib/server/nova/conversations");
    await archiveConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    return { success: true };
  });

export const unarchiveNovaConversation = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaMessagesSchema)
  .handler(async ({ data, context }) => {
    const { unarchiveConversation } =
      await import("~/lib/server/nova/conversations");
    await unarchiveConversation({
      conversationId: data.conversationId,
      coachId: context.coach.id,
    });
    return { success: true };
  });

export const getArchivedNovaConversations = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(getNovaConversationsSchema)
  .handler(async ({ data, context }) => {
    const { listConversations } =
      await import("~/lib/server/nova/conversations");
    return redactConversationList(
      context.coach,
      await listConversations({
        coachId: context.coach.id,
        agentType: data.agentType,
        scope: "archived",
      }),
    );
  });

export const loadNovaProfileValues = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(loadProfileValuesSchema)
  .handler(async ({ context }) => {
    const { loadCurrentProfileValues } =
      await import("~/lib/server/nova/apply");
    return loadCurrentProfileValues({ coachId: context.coach.id });
  });

export const applyNovaDeliverable = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(applyDeliverableSchema)
  .handler(async ({ data, context }) => {
    // Per-agent gate: walled only when the deliverable's phase isn't Diagnose.
    const { assertNovaDeliverableAccess } =
      await import("~/lib/server/nova/access");
    await assertNovaDeliverableAccess({
      coachId: context.coach.id,
      coach: context.coach,
      deliverableId: data.deliverableId,
    });
    const { applyDeliverableToProfile } =
      await import("~/lib/server/nova/apply");
    const result = await applyDeliverableToProfile({
      coachId: context.coach.id,
      deliverableId: data.deliverableId,
      fields: data.fields,
    });

    // Offer (never fire) a marketplace-approval prompt when real writes
    // landed and the coach is eligible for a fresh request — the UI must
    // capture explicit consent before any request is submitted (R29; the
    // Nova chat-approve incident is exactly why this never auto-fires here).
    const { canAutoRequestMarketplace } =
      await import("~/lib/server/coach-approval");
    const approvalPrompt =
      result.appliedCount > 0 && canAutoRequestMarketplace(context.coach);

    return { ...result, approvalPrompt };
  });

/**
 * Adapt an ICP deliverable into profile-ready field values.
 *
 * Returns a discriminated result rather than throwing on the expected
 * "no offer yet" precondition: the offer is produced in a later phase (a
 * separate conversation under the Diagnose/Plan split), so a coach can reach
 * this action before it exists. That case is normal, not a bug — surfacing it
 * as a typed result keeps it out of Sentry (DCM-NR) and lets the UI guide the
 * coach to finish their offer instead of showing a raw server error.
 */
export const adaptNovaDeliverable = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(adaptDeliverableSchema)
  .handler(async ({ data, context }) => {
    // Per-agent gate: walled only when the deliverable's phase isn't Diagnose.
    const { assertNovaDeliverableAccess } =
      await import("~/lib/server/nova/access");
    await assertNovaDeliverableAccess({
      coachId: context.coach.id,
      coach: context.coach,
      deliverableId: data.deliverableId,
    });
    const { resolveAdaptationInput } =
      await import("~/lib/server/nova/resolve-adaptation-input");
    const resolved = await resolveAdaptationInput({
      coachId: context.coach.id,
      deliverableId: data.deliverableId,
    });

    if (resolved.status === "missing_prerequisite") {
      return {
        status: "missing_prerequisite" as const,
        missing: resolved.missing,
      };
    }

    // Usage attribution: a ledger row needs the conversation's agent type
    // beside its id, and `resolveAdaptationInput` carries only the id. An ICP
    // with no source conversation stays off the ledger (check constraint).
    const { getConversation } = await import("~/lib/server/nova/conversations");
    const conversationRow = resolved.conversationId
      ? await getConversation({
          conversationId: resolved.conversationId,
          coachId: context.coach.id,
        })
      : null;

    const { adaptDeliverableForProfile } =
      await import("~/lib/server/nova/adapt-deliverable");
    const fields = await adaptDeliverableForProfile(resolved.input, {
      coachId: context.coach.id,
      conversation: conversationRow && {
        id: conversationRow.id,
        agentType: conversationRow.agentType,
      },
    });

    return { status: "ok" as const, fields };
  });

/**
 * Save a coach's hand-edit of a deliverable's free-text content (R1/R4).
 *
 * `assertNovaDeliverableAccess` deliberately no-ops on rows the coach does
 * not own (see its docstring) — cross-coach protection comes from
 * `editDeliverableContent`'s own coach-scoped source lookup, which returns a
 * typed `not_editable: "not_found"` result rather than throwing. The
 * discriminated result (`ok | no_change | conflict | not_editable |
 * validation_error`) is returned to the client as-is so the drawer can
 * render each state directly.
 */
export const updateNovaDeliverableContent = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(updateDeliverableContentSchema)
  .handler(async ({ data, context }) => {
    const { assertNovaDeliverableAccess } =
      await import("~/lib/server/nova/access");
    await assertNovaDeliverableAccess({
      coachId: context.coach.id,
      coach: context.coach,
      deliverableId: data.deliverableId,
    });

    const { editDeliverableContent } =
      await import("~/lib/server/nova/edit-deliverable");
    return editDeliverableContent({
      coachId: context.coach.id,
      deliverableId: data.deliverableId,
      expectedHeadId: data.expectedHeadId,
      edits: data.edits,
    });
  });

/**
 * What the coach still has in retired Nova formats: converted strategy sessions
 * in Past conversations, and parked deliverable types. Drives NOVA Home's note.
 */
export const getNovaRetiredWork = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { readRetiredWork } = await import("~/lib/server/nova/retired-work");
    return readRetiredWork(context.coach.id);
  });

/** The coach's own latest saved output in each retired format, to read. */
export const getNovaRetiredNotes = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { readRetiredNotes } = await import("~/lib/server/nova/retired-work");
    return readRetiredNotes(context.coach.id);
  });
